import { APP_INITIALIZER, ApplicationConfig, DEFAULT_CURRENCY_CODE, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TRANSLATE_HTTP_LOADER_CONFIG, TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Action, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { UserEffects } from './user/store/user.effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { AuthenticationService } from './shared/services/authentication.service';
import { AppState, reducers } from './core/store/core.state';
import { ConnectionsEffects } from './connections/store/connections.effects';
import { ConnectionsService } from './connections/services/connections.services';
import { PostsEffects } from './posts/store/posts.effects';
import { PostService } from './posts/services/posts.services';
import { provideToastr } from 'ngx-toastr';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import { CommentsService } from './comments/services/comments.services';
import { ToastService } from './shared/services/toast.service';
import { UsageTimerService } from './shared/services/usageTimer.service';
import { TimerEffects } from './core/store/timer.effects';
import { DatePipe } from '@angular/common';
import { InvitationsEffects } from './invitations/store/invitations.effects';
import { InvitationService } from './invitations/services/invitation.services';
import { MessageEffects } from './messages/store/messages.effects';
import { MessageService } from './messages/services/message.services';
import { TagsService } from './tags/services/tags.services';
import { TagsEffects } from './tags/store/tags.effects';
import { CommunitiesEffects } from './communities/store/communities.effects';
import { CommunitiesService } from './communities/services/communities.services';
import { ShoppingCartEffects } from './shoppingCart/store/shoppingCart.effects';
import { ShoppingCartService } from './shoppingCart/services/shoppingCart.service';
import { isDevMode } from '@angular/core';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { CheckoutEffects } from './checkout/store/checkout.effects';
import { CheckoutService } from './checkout/services/checkout.services';
import { CourseEffects } from './courses/store/course.effects';
import { CourseService } from './courses/services/course.service';
import { ContactsService } from './contacts/services/contacts.services';
import { OrderService } from './order/services/order.service';
import { OrderEffects } from './order/store/order.effects';
import { translateInitializer } from './shared/services/appTranslateInitializer.services';
import { loadingInterceptor } from './shared/interceptors/loading.interceptor';

// AoT requires an exported function for factories
export function HttpLoaderFactory() {
  return new TranslateHttpLoader();
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: translateInitializer,
      deps: [TranslateService],
      multi: true
    },
    provideRouter(routes),
    importProvidersFrom(HttpClientModule, TranslateModule.forRoot({
        loader: {
            provide: TranslateLoader,
            useFactory: HttpLoaderFactory,
            deps: [],
        }
    })),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: '/assets/i18n/',
        suffix: '.json',
        enforceLoading: isDevMode(),
        useHttpBackend: false
      }
    },
    provideStore<AppState, Action>(reducers),
    provideEffects(
      [
        UserEffects,
        ConnectionsEffects,
        PostsEffects,
        TimerEffects,
        InvitationsEffects,
        MessageEffects,
        TagsEffects,
        CommunitiesEffects,
        ShoppingCartEffects,
        CheckoutEffects,
        CourseEffects,
        OrderEffects
      ]),
    provideStoreDevtools(),
    AuthenticationService,
    ConnectionsService,
    InvitationService,
    PostService,
    CommentsService,
    MessageService,
    provideToastr(),
    provideAnimations(),
    ToastService,
    UsageTimerService,
    DatePipe,
    TagsService,
    CommunitiesService,
    provideTranslocoLocale(),
    ShoppingCartService,
    CheckoutService,
    CourseService,
    ContactsService,
    OrderService,
    provideHttpClient(withInterceptors([loadingInterceptor])),
    provideTransloco({
        config: {
          availableLangs: ['en', 'es', 'pt-BR', 'pt-PT'],
          defaultLang: 'en',
          // Remove this option if your application doesn't support changing language in runtime.
          reRenderOnLangChange: true,
          prodMode: !isDevMode(),
        },
        loader: TranslocoHttpLoader
      })
  ],
};

//https://stackoverflow.com/questions/44287827/dynamically-change-locale-for-datepipe-in-angular-2

